/**
 * FERP Portal - Entity Access Tracker
 *
 * Tracks per-user, per-subgraph entity type access frequency in localStorage.
 * Enables personalized navigation: frequently accessed entity types bubble
 * up to become default views in the SubgraphBrowserPage.
 *
 * FERP Principles:
 * - Data-driven: Personalization from actual user behavior, not assumptions
 * - No sensitive data: Only stores entity type names and counts
 * - Graceful degradation: No-op if not logged in or localStorage unavailable
 *
 * localStorage key: ferp:nav:{userId}:{subgraphId}
 *
 * Future (Phase 1b): Backend sync via Analytics primitive for cross-device persistence
 */

import { useCallback, useRef } from 'react';
import { useAuth } from '@/auth';

/** Minimum total accesses before personalization kicks in */
const MIN_ACCESSES_FOR_PERSONALIZATION = 5;

/** Debounce window: ignore duplicate accesses within this period */
const DEBOUNCE_MS = 5000;

/** localStorage key prefix */
const STORAGE_PREFIX = 'ferp:nav';

/**
 * Single access record for an entity type within a subgraph.
 */
export interface EntityAccessRecord {
  /** graphqlType name, e.g. "Patient" */
  entityType: string;
  /** Total number of times the user navigated to this entity type */
  accessCount: number;
  /** ISO 8601 timestamp of last access */
  lastAccessedAt: string;
}

/**
 * Per-subgraph access data stored in localStorage.
 */
interface SubgraphAccessData {
  subgraphId: string;
  records: Record<string, EntityAccessRecord>;
  /** ISO 8601 timestamp of when this data was last synced to backend */
  lastSyncedAt: string | null;
  /** Schema version for future migrations */
  version: 1;
}

export interface UseEntityAccessTrackerReturn {
  /** Record a view of an entity type (call on selection) */
  recordAccess: (subgraphId: string, entityType: string) => void;
  /** Get access records for a subgraph, sorted by accessCount descending */
  getFrequentTypes: (subgraphId: string) => EntityAccessRecord[];
  /** Whether there is enough data to personalize (>= threshold accesses) */
  hasPersonalizationData: (subgraphId: string) => boolean;
  /** Clear all tracking data for current user */
  clearAll: () => void;
}

function buildStorageKey(userId: string, subgraphId: string): string {
  return `${STORAGE_PREFIX}:${userId}:${subgraphId}`;
}

function readData(userId: string, subgraphId: string): SubgraphAccessData | null {
  try {
    const key = buildStorageKey(userId, subgraphId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SubgraphAccessData;
    if (parsed.version !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeData(userId: string, subgraphId: string, data: SubgraphAccessData): void {
  try {
    const key = buildStorageKey(userId, subgraphId);
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // localStorage full or unavailable — fail silently
  }
}

/**
 * Tracks entity type access frequency in localStorage.
 *
 * Usage:
 * ```tsx
 * const tracker = useEntityAccessTracker();
 * // When user selects an entity type:
 * tracker.recordAccess('fhir', 'Patient');
 * // Get ordered frequent types:
 * const frequent = tracker.getFrequentTypes('fhir');
 * ```
 */
export function useEntityAccessTracker(): UseEntityAccessTrackerReturn {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  // Ref for debounce tracking (avoids triggering re-renders)
  const lastAccessRef = useRef<{ key: string; time: number }>({ key: '', time: 0 });

  const recordAccess = useCallback((subgraphId: string, entityType: string): void => {
    if (!userId) return;

    // Debounce: skip if same entity accessed within DEBOUNCE_MS
    const debounceKey = `${subgraphId}:${entityType}`;
    const now = Date.now();
    if (lastAccessRef.current.key === debounceKey && now - lastAccessRef.current.time < DEBOUNCE_MS) {
      return;
    }
    lastAccessRef.current = { key: debounceKey, time: now };

    const existing = readData(userId, subgraphId) ?? {
      subgraphId,
      records: {},
      lastSyncedAt: null,
      version: 1 as const,
    };

    const record = existing.records[entityType] ?? {
      entityType,
      accessCount: 0,
      lastAccessedAt: '',
    };
    record.accessCount += 1;
    record.lastAccessedAt = new Date().toISOString();
    existing.records[entityType] = record;

    writeData(userId, subgraphId, existing);
  }, [userId]);

  const getFrequentTypes = useCallback((subgraphId: string): EntityAccessRecord[] => {
    if (!userId) return [];
    const data = readData(userId, subgraphId);
    if (!data) return [];
    return Object.values(data.records)
      .sort((a, b) => b.accessCount - a.accessCount);
  }, [userId]);

  const hasPersonalizationData = useCallback((subgraphId: string): boolean => {
    if (!userId) return false;
    const data = readData(userId, subgraphId);
    if (!data) return false;
    const totalAccesses = Object.values(data.records)
      .reduce((sum, r) => sum + r.accessCount, 0);
    return totalAccesses >= MIN_ACCESSES_FOR_PERSONALIZATION;
  }, [userId]);

  const clearAll = useCallback((): void => {
    if (!userId) return;
    const prefix = `${STORAGE_PREFIX}:${userId}:`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  }, [userId]);

  return { recordAccess, getFrequentTypes, hasPersonalizationData, clearAll };
}

export default useEntityAccessTracker;
